'use client';

import type { ReactNode } from 'react';
import React, {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../utils/cx';

export interface TooltipProps {
  content: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * When true (default), renders into document.body to avoid overflow clipping.
   * When false, renders inline (not portaled) and can be clipped by ancestor overflow.
   */
  portal?: boolean;
  children: ReactNode;
  className?: string;
}

type Side = NonNullable<TooltipProps['side']>;
type Pos = { top: number; left: number };
type Placement = { pos: Pos; transform: string };

/**
 * Merge two event handlers into one, preserving both.
 */
function mergeHandler<T extends (...args: any[]) => void>(a?: T, b?: T): T | undefined {
  if (!a) return b;
  if (!b) return a;

  return (((...args: any[]) => {
    a(...args);
    b(...args);
  }) as unknown) as T;
}

/**
 * Safely assign to a React ref (callback ref or RefObject).
 */
function setRefValue<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') ref(value);
  else if (typeof ref === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ref as any).current = value;
  }
}

/**
 * Compose multiple refs into a single callback ref.
 */
function composeRefs<T>(...refs: Array<React.Ref<T> | undefined>): React.RefCallback<T> {
  return (value) => {
    for (const ref of refs) setRefValue(ref, value);
  };
}

function mergeDescribedBy(existing: unknown, nextId: string): string {
  const base = typeof existing === 'string' ? existing.trim() : '';
  if (!base) return nextId;

  const parts = base.split(/\s+/);
  if (parts.includes(nextId)) return base;
  return `${base} ${nextId}`;
}

function computePlacement(rect: DOMRect, side: Side): Placement {
  const offset = 12;

  switch (side) {
    case 'top':
      return {
        pos: { top: rect.top - offset, left: rect.left + rect.width / 2 },
        transform: '-translate-x-1/2 -translate-y-full',
      };
    case 'bottom':
      return {
        pos: { top: rect.bottom + offset, left: rect.left + rect.width / 2 },
        transform: '-translate-x-1/2',
      };
    case 'left':
      return {
        pos: { top: rect.top + rect.height / 2, left: rect.left - offset },
        transform: '-translate-x-full -translate-y-1/2',
      };
    case 'right':
    default:
      return {
        pos: { top: rect.top + rect.height / 2, left: rect.right + offset },
        transform: '-translate-y-1/2',
      };
  }
}

/**
 * Tooltip
 * - Hover/focus
 * - Default portal=true (per spec)
 * - Uses aria-describedby on the trigger element when possible (single child element).
 *
 * React 19 note:
 * - Do not access element.ref. Refs are treated as regular props; use child.props.ref instead.
 */
export function Tooltip({ children, content, side = 'right', portal = true, className }: TooltipProps) {
  const rid = useId();
  const tooltipId = useMemo(() => `tooltip-${rid}`, [rid]);

  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement>({ pos: { top: 0, left: 0 }, transform: '' });

  const triggerRef = useRef<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLSpanElement | null>(null);

  const rafIdRef = useRef<number | null>(null);

  const update = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const next = computePlacement(rect, side);

    // If rendering inline (not portaled), position within the wrapper coordinate space.
    if (!portal) {
      const wrapper = wrapperRef.current;
      if (wrapper) {
        const wRect = wrapper.getBoundingClientRect();
        next.pos = {
          top: next.pos.top - wRect.top,
          left: next.pos.left - wRect.left,
        };
      }
    }

    setPlacement(next);
  }, [portal, side]);

  const scheduleUpdate = useCallback(() => {
    if (rafIdRef.current != null) return;
    rafIdRef.current = window.requestAnimationFrame(() => {
      rafIdRef.current = null;
      update();
    });
  }, [update]);

  const show = useCallback(() => {
    update();
    setOpen(true);
  }, [update]);

  const hide = useCallback(() => {
    setOpen(false);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    update();
  }, [open, update]);

  useEffect(() => {
    if (!open) return;

    const onScroll = () => scheduleUpdate();
    const onResize = () => scheduleUpdate();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };

    // Capture scroll from nested containers; rAF throttle avoids excessive layout work.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [hide, open, scheduleUpdate]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current != null) window.cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  if (!content) return <>{children}</>;

  const tooltipNode = (
    <span
      id={tooltipId}
      className={cx(
        'pointer-events-none z-[9999] whitespace-nowrap rounded-md',
        'bg-primary-solid text-white',
        'ring-1 ring-secondary_alt ring-inset',
        'px-2.5 py-1.5 text-xs font-medium shadow-lg',
        portal ? 'fixed' : 'absolute',
        placement.transform,
        className,
      )}
      style={{ top: `${placement.pos.top}px`, left: `${placement.pos.left}px` }}
      role="tooltip"
    >
      {content}
    </span>
  );

  const renderedTooltip = open
    ? portal && typeof document !== 'undefined'
      ? createPortal(tooltipNode, document.body)
      : tooltipNode
    : null;

  // Best a11y: clone single element child and add aria-describedby + handlers + ref.
  if (isValidElement(children)) {
    const child = children as React.ReactElement<Record<string, unknown>>;
    const childProps = child.props ?? {};

    // React 19: refs are regular props; do not access element.ref.
    const existingRef = (childProps as { ref?: React.Ref<HTMLElement> }).ref;
    const composedRef = composeRefs<HTMLElement>(existingRef, (node) => {
      triggerRef.current = node;
    });

    const patched = cloneElement(child, {
      ref: composedRef,
      onMouseEnter: mergeHandler(childProps.onMouseEnter as any, show),
      onMouseLeave: mergeHandler(childProps.onMouseLeave as any, hide),
      onFocus: mergeHandler(childProps.onFocus as any, show),
      onBlur: mergeHandler(childProps.onBlur as any, hide),
      'aria-describedby': open ? mergeDescribedBy((childProps as any)['aria-describedby'], tooltipId) : (childProps as any)['aria-describedby'],
    });

    // For inline tooltips we need a positioned ancestor. Wrap only in that mode to avoid DOM changes in portal mode.
    if (!portal) {
      return (
        <span ref={wrapperRef} className="relative inline-flex">
          {patched}
          {renderedTooltip}
        </span>
      );
    }

    return (
      <>
        {patched}
        {renderedTooltip}
      </>
    );
  }

  // Fallback: wrapper span (safe for multiple nodes)
  return (
    <span ref={wrapperRef} className={cx(!portal && 'relative inline-flex')}>
      <span
        ref={(n) => {
          triggerRef.current = n;
        }}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={open ? tooltipId : undefined}
      >
        {children}
      </span>

      {renderedTooltip}
    </span>
  );
}

Tooltip.displayName = 'Tooltip';
